/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {contains} from '@ion/core/src/utils/StringUtil';
import {isCustomTheme} from '../useExternalTheme';
import {getSimulatedReseller} from './ResellerDevTools';

/**
 * Defines the list of CalAmp exclusion domains which are not be be included
 * when determining if the application is running in Co-Branding (Reseller)
 * mode. By default, iOn Applications are deployed to application specific
 * calamp.com subdomains, such as `ion.calamp.com` and `admin.calamp.com`.
 * Likewise, customer co-branded applications are hosted and deployed to the
 * `calamp.com` domain with customer specific subdomain, such as Assist Cargo,
 * which is available at: `assistcargo.calamp.com`.
 */
const exclusions = ['ion', 'admin'];

/**
 * Provides a global utility which determines if the application is running
 * as a CalAmp Co-Branding instance (Reseller).
 *
 * The logic to determine if the application is a Reseller instance is based
 * on the application being deployed to CalAmp Customer specific domain, and
 * an external `theme.json` document has been loaded.
 *
 * Note: As mentioned above, the `isReseller` API has a direct dependency on
 * the value of `isCustomTheme` (which will be set to `true` if an external
 * `theme.json` document has been loaded). This dependency is necessary as
 * applications pass a list of domain exclusions (such as `qa.ion.calamp.com`,
 * etc.) to the `useExternalTheme` hook, which must be taken into account;
 * however, the `isReseller` API does not maintain state and thus will not
 * have a reference to the domains which should be excluded. The optional
 * `includeCustomTheme` parameter can be set to `false` should clients
 * which to not take the domain exclusions into account during validation.
 *
 * When running in local development mode, the domain check is based on the
 * application being deployed to the localhost loopback address (127.0.0.1),
 * which is the default address used when testing co-branded resellers locally.
 *
 * ```typescript
 *   import isReseller from '@ion/dls/src/resellers';
 *   ...
 *   if (isReseller()) {
 *     // reseller specific implementation ...
 *   }
 *   // without domain exclusions included ...
 *   if (isReseller(false)) {
 *     // reseller specific implementations  ...
 *   }
 * ```
 *
 * @param   includeCustomTheme When `true` (default), will require a call to the
 *          `isCustomTheme` function to return true in addition to the internal
 *          domain validation. If false, only internal domain validation will be
 *          performed.
 * @returns `true` if the application is running as a CalAmp Co-Branding instance
 *          (Reseller), otherwise, `false`.
 */
export const isReseller = (includeCustomTheme = true): boolean => {
  const isValidDomain = Boolean(getResellerDomain());
  if (includeCustomTheme) {
    return isValidDomain && isCustomTheme();
  }
  return isValidDomain;
};

/**
 * Provides a utility which returns the name of the CalAmp Co-Branding (Reseller)
 * Customer, otherwise, returns `undefined`. When running locally, naturally the
 * reseller domain will not be accessible from localhost, thus the `reseller`
 * querystring parameter will be used, if provided.
 *
 * Note: The function has a direct dependency on the value of `isCustomTheme`
 * (which will be set to `true` if an external`theme.json` document has been
 * loaded). This dependency is necessary as applications pass a list of domain
 * exclusions (such as `qa.ion.calamp.com`, etc.) to the `useExternalTheme`
 * hook, which must be taken into account; however, the `isReseller` API does
 * not maintain state and thus will not have a reference to the domains which
 * should be excluded. The optional `includeCustomTheme` parameter can be set
 * to `false` should clients which to not take the domain exclusions into account
 * during validation.
 *
 * ```typescript
 *   import {getResellerName} from '@ion/dls/src/resellers';
 *   ...
 *
 *   // example production url: https://assistcargo.calamp.com
 *   const customer = getResellerName(); // "assistcargo"
 *
 *   // example pre-production url: https://dev-assistcargo.calamp.com
 *   const customer = getResellerName(); // "assistcargo"
 *
 *   ...
 *   // example local url: http://127.0.0.1:8080?reseller=assistcargo
 *   const customer = getResellerName(); // "assistcargo"
 * ```
 *
 * @param   includeCustomTheme When `true` (default), will require a call to the
 *          `isCustomTheme` function to return true in addition to the internal
 *          domain validation. If false, only internal domain validation will be
 *          performed.
 * @returns The CalAmp Co-Branding (Reseller) customer name based on the deployed
 *          sub-domain environment, otherwise, `undefined`.
 */
export const getResellerName = (includeCustomTheme = true): string => {
  if (!includeCustomTheme || (includeCustomTheme && isCustomTheme())) {
    return normalizeDomainName(getResellerDomain());
  }
};

/**
 * Provides a utility which returns the filename (excluding extension) for the
 * CalAmp Reseller translation document.
 *
 * By default, all customer reseller translation documents are prefixed with
 * `reseller-`. This default can be overridden via the prefix argument.
 *
 * ```typescript
 *   import {getTranslationsFilename} from '@ion/dls/src/resellers';
 *   ...
 *
 *   // example production url: https://assistcargo.calamp.com
 *   const customer = getTranslationsFilename(); // "reseller-assistcargo"
 *
 *   // example pre-production url: https://dev-assistcargo.calamp.com
 *   const customer = getTranslationsFilename(); // "reseller-assistcargo"
 *
 *   ...
 *   // example local url: http://127.0.0.1:8080?reseller=assistcargo
 *   const customer = getTranslationsFilename(); // "reseller-assistcargo"
 * ```
 *
 * @param   prefix An optional prefix to be appended to the customer translation
 *          document name.
 * @returns The translation document name for the CalAmp Reseller.
 */
export const getTranslationsFilename = (prefix = 'reseller') => {
  if (isReseller(false)) {
    return `${prefix}-${getResellerName(false)}`;
  }
};

/**
 * Provides a convenience function which allows for applying a reseller translation
 * document to the given list of translations to be included.
 *
 * ```typescript
 *   import {applyTranslations} from '@ion/dls/src/theme/resellers/Resellers';
 *
 *   ...
 *   // application is deployed to 'assistcargo' reseller domain ...
 *   const translations = applyTranslations(['login','common']);
 *   translations[0]; // 'login'
 *   translations[1]; // 'common'
 *   translations[0]; // 'reseller-assistcargo'
 *
 *
 *   // application is not deployed to a reseller domain ...
 *   const translations = applyTranslations(['login','common']);
 *   translations[0]; // 'login'
 *   translations[1]; // 'common'
 *
 *   ...
 *   const LoginPage = withTranslation(translations))(Page) as PageLayoutConsumer;
 * ```
 *
 * @param   translations The list of translation modules to be included
 * @param   prefix An optional prefix to be appended to the customer translation
 *          document name.
 * @param   resolver An optional function which resolves the translation file to
 *          be used. This is useful for cases where the reseller translation file
 *          does not contain the host name, but is mapped to an alias for simplicity,
 *          such as `https://some-long-name-reseller.com` can be aliased to simply
 *          `reseller-slnr.json`, etc..
 * @returns the provided list of translations with the given resellers translation
 *          module added when `isReseller` is `true`.
 */
export const applyTranslations = (
  translations: string[],
  prefix = 'reseller',
  resolver = getTranslationsFilename): string[] => {
  if (isReseller(false)) {
    return [...translations, resolver(prefix)];
  }
  return translations;
};

/**
 * Provides a utility which normalizes the reseller domain name by removing any
 * pre-prod environment specific prefixes; e.g. `dev-assistcargo' will be resolved
 * to `assistcargo`, etc..
 */
const normalizeDomainName = (domain: string): string => {
  if (domain) {
    return domain.replace(/(dev|qa|stg)[.-]/g, '');
  }
  return domain;
};

/**
 * Provides a utility which returns the name of the CalAmp Co-Branding (Reseller)
 * Sub-Domain.
 *
 * Note: During local development, the reseller domain is simulated and resolved
 * based on the initial `?reseller=<customer-name>` parameter provided to the
 * application query string.
 *
 * @returns The CalAmp Co-Branding (Reseller) sub-domain based on the deployed
 *          environment.
 */
const getResellerDomain = (): string => {
  const simulated  = getSimulatedReseller();
  const {hostname} = globalThis?.location || {};
  if (hostname && !simulated) {
    const domain = normalizeDomainName(hostname.split('.calamp')[0]);
    if (!exclusions.some(exclusion => exclusion === domain)) {
      return domain;
    }
  }
  return simulated;
};

export default isReseller;
